import { useCallback, useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import blackHistory from '#src/assets/wordmark/blackHistory.svg';
import pride from '#src/assets/wordmark/pride.svg';

import { useContractModalCta } from '#src/app/pages/ambassadors/landing/shared/hooks';
import { useSessionConfiguration } from '#src/app/shared/hooks/sessionConfiguration';

type Result = ModelHookResult<
	{
		logoUrl: string | undefined;
	},
	{
		handleCtaClick: () => void;
		handleLogoClick: () => void;
	}
>;

/**
 * Provides the ambassadors landing page header's state and handlers: the campaign logo URL to
 * swap in (if any), and click handlers for the logo and the CTA.
 *
 * Mirrors the campaign-logo swap logic from the default site header's Logo
 * (src/app/app/layout/components/Header/components/Logo/hooks/useLogo) since that
 * component isn't exported from the `#src/app/app/layout` barrel for reuse here.
 */
export function useAmbassadorsHeader(): Result {
	const { featureConfigs } = useSessionConfiguration();
	const track = useTracking();

	const logoUrl = useMemo(() => {
		if (featureConfigs.showBlackHistoryLogo) return blackHistory;
		if (featureConfigs.showPrideLogo) return pride;
		return undefined;
	}, [featureConfigs]);

	const handleLogoClick = useCallback(() => {
		void track('ambassadors_header_logo_click');
	}, [track]);

	return {
		data: {
			logoUrl,
		},
		actions: {
			handleCtaClick: useContractModalCta('header'),
			handleLogoClick,
		},
	};
}
